"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.updateSearchParams = exports.searchUrlBuilder = exports.removeQueryParamsFromPath = exports.rebuildPathWithParams = exports.productUrlBuilder = exports.contentUrlBuilder = exports.categoryUrlBuilder = exports.buildUrlSet = exports.buildSuggestionUrl = exports.absoluteUrl = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _url = require("pwa-kit-react-sdk/utils/url");
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { (0, _defineProperty2.default)(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; } /*
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          * Copyright (c) 2022, salesforce.com, inc.
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          * All rights reserved.
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          * SPDX-License-Identifier: BSD-3-Clause
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          */ // @ts-ignore
/**
 * A function that takes a path and qualifies it with the current host and protocol.
 * This function works on the client and on the server.
 *
 * @example
 * absoluteUrl(/women/dresses?color=black)
 *
 * // => http(s)://www.site.com/women/dresses?color=black
 * @param path
 * @returns {string|*}
 */
const absoluteUrl = path => {
  return new URL(path, (0, _url.getAppOrigin)()).toString();
};

/**
 * Modifies a given url by adding/updating query parameters.
 *
 * @param {string} url - The base url of the output url set.
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string} A URL with additional params
 * @example
 * import {rebuildPathWithParams} from '/path/to/utils/url'
 *
 * rebuildPathWithParams(
 *     '/en-GB/product/25501032M',
 *     {color: 'JJ2SKXX', size: 'MD'}
 * )
 *
 * // Returns
 * // '/en-GB/product/25501032M?color=JJ2SKXX&size=9MD'
 */
exports.absoluteUrl = absoluteUrl;
const rebuildPathWithParams = (url, extraParams) => {
  const [pathname, search] = url.split('?');
  const params = new URLSearchParams(search);
  updateSearchParams(params, extraParams);

  // Clean up any trailing `=` for params without values.
  const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '');

  // Generate the newly updated url.
  return `${pathname}${Array.from(paramStr).length > 0 ? `?${paramStr}` : ''}`;
};
exports.rebuildPathWithParams = rebuildPathWithParams;
const updateSearchParams = (searchParams, newParams) => {
  Object.entries(newParams).forEach(([key, value]) => {
    // 0 is a valid value as for a param
    if (!value && value !== 0) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, value);
    }
  });
};

/**
 * Builds a list of modified Urls with the provided params key and values,
 * preserving any search params provided in the original url.Optionally
 * you can pass and object used to set static params values.
 * @param {string} url - The base url of the output url set.
 * @param {string} key - The search params for the associated values
 * @param {Array} values - The search param values
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string[]} A list of URLs
 * @example
 * import {buildUrlSet} from '/path/to/utils/url'
 *
 * buildUrlSet(
 *     '/womens/clothing',
 *     'sort',
 *     ['price-high-to-low', 'price-low-to-high'],
 *     {offset: 0}
 * )
 *
 * // Returns
 * // ['/womens/clothing?sort=price-high-to-low', '/womens/clothing?sort=price-low-to-high']
 */
exports.updateSearchParams = updateSearchParams;
const buildUrlSet = (url = '', key = '', values = [], extraParams = {}) => values.map(value => rebuildPathWithParams(url, _objectSpread({
  [key]: value
}, extraParams)));

/**
 * Given a content page and the current locale returns an href to its page.
 *
 * @param {Object} page
 * @returns {string}
 */
exports.buildUrlSet = buildUrlSet;
const contentUrlBuilder = page => {
  if (page.headlessUrl) {
    return encodeURI(page.headlessUrl);
  }
  return encodeURI(`/${page.url ? page.url : page.id}`);
};

/**
 * Given a category and the current locale returns an href to the product list page.
 *
 * @param {Object} category
 * @returns {string}
 */
exports.contentUrlBuilder = contentUrlBuilder;
const categoryUrlBuilder = category => encodeURI(`/shop/${category.id}`);
exports.categoryUrlBuilder = categoryUrlBuilder;
function formatNameToUrl(name) {
  return (name || '').replace(/[\\#,+()$~%.'":*?<>{}´`]/g, '').toLowerCase().replace(/[&/\s]/g, '-');
}

/**
 * Given a product and the current locale returns an href to the product detail page.
 *
 * @param {Object} product
 * @returns {string}
 */
const productUrlBuilder = product => {
  const formattedName = formatNameToUrl(product.name);
  let url = `/${formattedName}-${product.id}`;
  if (product.primaryCategoryName && product.classCategoryName !== product.primaryCategoryName) {
    url = `/${product.primaryCategoryPath || formatNameToUrl(product.primaryCategoryName)}${url}`;
  }
  if (product.classCategoryName) {
    url = `/${product.classCategoryPath || formatNameToUrl(product.classCategoryName)}${url}`;
  }
  return url;
};

/**
 * Given a search term, constructs a search url.
 *
 * @param {string} searchTerm
 * @returns {string}
 */
exports.productUrlBuilder = productUrlBuilder;
const searchUrlBuilder = searchTerm => `/search?q=${searchTerm}`;

/*
 * Remove query params from a give url path based on a given list of keys
 *
 * @param {string} path - The part of url to have params removed from.
 * @param {array} keys - list of params to be removed
 * @returns {string} - the url after param has been removed
 * @example
 * import {removeQueryParamsFromPath} from /path/to/util/url
 *
 * removeQueryParamsFromPath(
 *   /en-GB/cart?pid=1234&color=black&size=s&abc=12,
 *   ['pid', 'color', 'size']
 * )
 * // returns
 * // '/en-GB/cart?abc=12'
 */
exports.searchUrlBuilder = searchUrlBuilder;
const removeQueryParamsFromPath = (path, keys) => {
  const [pathname, search] = path.split('?');
  const params = new URLSearchParams(search);
  keys.forEach(key => {
    if (params.has(key)) {
      params.delete(key);
    }
  });

  // Clean up any trailing `=` for params without values.
  const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '');
  return `${pathname}${paramStr && '?'}${paramStr}`;
};

/**
 * Get the url of a suggestion item
 * @param {String} suggestionType - the suggestion type
 * @param {Object} suggestionItem - the suggestion item
 * return {String} - the url of the suggestion item
 */
exports.removeQueryParamsFromPath = removeQueryParamsFromPath;
const buildSuggestionUrl = (suggestionType, suggestionItem, buildUrl) => {
  const suggestions = {
    brandSuggestions: item => buildUrl(searchUrlBuilder(item.id)),
    categorySuggestions: item => buildUrl(categoryUrlBuilder(item)),
    contentSuggestions: item => buildUrl(contentUrlBuilder(item)),
    popularSuggestions: item => buildUrl(searchUrlBuilder(item.id)),
    productSuggestions: item => buildUrl(productUrlBuilder(item)),
    recentSearchPhrases: item => buildUrl(searchUrlBuilder(item.id)),
    recentSuggestions: item => buildUrl(searchUrlBuilder(item.id))
  };
  // @ts-ignore
  return suggestions[suggestionType] ?
  // @ts-ignore
  suggestions[suggestionType](suggestionItem) : console.error(`${suggestionType} is not a valid suggestion type`);
};
exports.buildSuggestionUrl = buildSuggestionUrl;